import React from 'react';
import { useShippingMethods, ShippingMethod } from '../hooks/useShippingMethods';
import { useOrderPersonalization } from '@/shared/hooks/useOrderPersonalization';

// Helper pour extraire la première couleur d'un gradient ou retourner la couleur unie
const getPrimaryColor = (colorValue: string | undefined): string => {
  if (!colorValue) return '#9333EA';
  if (colorValue.includes('gradient') || colorValue.includes('linear-gradient')) {
    const colorsMatch = colorValue.match(/#[0-9A-Fa-f]{3,6}/g);
    if (colorsMatch && colorsMatch.length > 0) {
      return colorsMatch[0];
    }
  }
  return colorValue;
};

// Helper pour créer une couleur avec opacité (support gradient)
const getColorWithOpacity = (colorValue: string | undefined, opacity: number = 0.1): string => {
  const primaryColor = getPrimaryColor(colorValue);
  if (primaryColor.startsWith('#')) {
    const hex = primaryColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return primaryColor;
};

interface ShippingMethodSelectorProps {
  selectedMethod: string | null;
  onMethodChange: (methodId: string) => void;
  selectedCountry?: string;
  className?: string;
}

const ShippingMethodSelector: React.FC<ShippingMethodSelectorProps> = ({
  selectedMethod,
  onMethodChange,
  selectedCountry,
  className = ''
}) => {
  const { getShippingMethodsForCountry, loading, error } = useShippingMethods();
  const personalization = useOrderPersonalization();

  // Filtrer les méthodes selon le pays sélectionné
  const methodsForCountry = selectedCountry
    ? getShippingMethodsForCountry(selectedCountry)
    : [];

  // Log uniquement quand le pays change
  React.useEffect(() => {
    if (selectedCountry) {
      console.log('🚚 ShippingMethodSelector - selectedCountry changed:', selectedCountry);
    }
  }, [selectedCountry]);

  // Effet pour sélectionner automatiquement s'il n'y a qu'une seule méthode disponible
  React.useEffect(() => {
    if (methodsForCountry.length === 1) {
      const singleMethod = methodsForCountry[0];
      // Sélectionner automatiquement si aucune méthode n'est sélectionnée
      // ou si la méthode sélectionnée ne fait pas partie des méthodes disponibles
      const isCurrentMethodValid = selectedMethod && methodsForCountry.some(m => m.id === selectedMethod);
      if (!isCurrentMethodValid) {
        // On utilise un timeout pour éviter les mises à jour d'état pendant le rendu
        const timer = setTimeout(() => {
          onMethodChange(singleMethod.id);
        }, 0);
        return () => clearTimeout(timer);
      }
    }
  }, [methodsForCountry, selectedMethod, onMethodChange]);

  if (loading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div
          className="h-4 rounded w-1/4 mb-2"
          style={{ backgroundColor: personalization.input.background || '#E5E7EB' }}
        ></div>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-16 rounded"
              style={{ backgroundColor: personalization.input.background || '#E5E7EB' }}
            ></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`border rounded-lg p-4 ${className}`}
        style={{
          backgroundColor: `${personalization.button.primary || '#F59E0B'}10`,
          borderColor: personalization.button.primary || '#F59E0B',
          color: personalization.text.primary || '#92400E',
          borderRadius: personalization.card.borderRadius || '0.5rem',
        }}
      >
        <p className="font-medium">⏳ Méthodes de livraison en configuration</p>
        <p className="text-sm mt-2" style={{ color: personalization.text.secondary || '#78350F' }}>
          Les méthodes de livraison sont en cours de configuration par le vendeur.
        </p>
      </div>
    );
  }

  if (!selectedCountry) {
    return (
      <div
        className={`border rounded-lg p-4 ${className}`}
        style={{
          backgroundColor: `${personalization.button.primary || '#2563EB'}10`,
          borderColor: personalization.button.primary || '#2563EB',
          color: personalization.button.primary || '#2563EB',
          borderRadius: personalization.card.borderRadius || '0.5rem',
        }}
      >
        <p className="font-medium">🌍 Sélectionnez un pays</p>
        <p className="text-sm mt-2" style={{ color: personalization.text.secondary || '#1E40AF' }}>
          Veuillez d'abord sélectionner un pays pour voir les méthodes de livraison disponibles.
        </p>
      </div>
    );
  }

  if (!methodsForCountry || methodsForCountry.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <div className="space-y-2">
        {methodsForCountry.map((method: ShippingMethod) => {
          // S'assurer que la comparaison fonctionne en convertissant les deux en string
          const isSelected = String(selectedMethod) === String(method.id);
          return (
            <label
              key={method.id}
              className="flex items-start p-3 border rounded-lg cursor-pointer transition-all"
              style={{
                borderColor: isSelected
                  ? getPrimaryColor(personalization.button.primary)
                  : (personalization.input.border || '#E5E7EB'),
                backgroundColor: isSelected
                  ? getColorWithOpacity(personalization.button.primary, 0.1)
                  : 'transparent',
                borderRadius: personalization.card.borderRadius || '0.5rem',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = personalization.input.border || '#D1D5DB';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = personalization.input.border || '#E5E7EB';
                }
              }}
            >
              <input
                type="radio"
                name="shippingMethod"
                className="mt-1 mr-3 focus:ring-2"
                style={{
                  color: getPrimaryColor(personalization.button.primary),
                  '--tw-ring-color': getPrimaryColor(personalization.button.primary),
                } as any}
                checked={isSelected}
                onChange={() => onMethodChange(method.id)}
              />
              <div className="flex-1 flex items-center justify-between">
                <div className="flex-1">
                  <h4
                    className="font-medium text-sm"
                    style={{ color: personalization.text.primary }}
                  >
                    {method.name}
                  </h4>
                  <p
                    className="text-xs mt-1"
                    style={{ color: personalization.text.secondary }}
                  >
                    {method.description}
                  </p>
                  <div className="mt-2">
                    <p
                      className="text-xs"
                      style={{ color: personalization.text.secondary }}
                    >
                      {method.estimatedHours > 0
                        ? `Délai: ${Math.ceil(method.estimatedHours / 24)} jour(s) ouvré(s)`
                        : 'Délai: 1 jour (préparation)'
                      }
                    </p>
                    {method.isHomeDelivery !== undefined && (
                      <div className="mt-1">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                          style={{
                            backgroundColor: method.isHomeDelivery
                              ? `${getPrimaryColor(personalization.button.primary)}20`
                              : personalization.badge.background || '#F1F5F9',
                            color: method.isHomeDelivery
                              ? getPrimaryColor(personalization.button.primary)
                              : personalization.badge.text || '#475569',
                            borderRadius: personalization.badge.borderRadius || '0.375rem',
                          }}
                        >
                          {method.isHomeDelivery ? '🏠 Domicile' : '📍 Relais'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right ml-4">
                  <div
                    className="font-semibold whitespace-nowrap text-sm"
                    style={{ color: personalization.text.heading }}
                  >
                    {(() => {
                      const price = selectedCountry ? method.getPriceForCountry(selectedCountry) : method.price;
                      return price > 0 ? `${price.toFixed(2)} €` : 'Gratuit';
                    })()}
                  </div>
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(ShippingMethodSelector);
